import { memo, useRef, forwardRef, useImperativeHandle, useCallback, useEffect } from 'react'
import { ENV } from '@/utils'
import { Box } from '../../Layout'
import { Text } from '../../Data'
import { ReCaptchaProps, ReCaptchaInstance } from './ReCaptcha.types'
import { reCaptchaStyles as styles } from './ReCaptcha.styles'

const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-explicit'
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'

const ReCaptcha = forwardRef<ReCaptchaInstance, ReCaptchaProps>(
  ({ onChange, onExpired, onError, errorText }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<number | null>(null)

    useImperativeHandle(ref, () => ({
      reset: () => window.grecaptcha?.reset(widgetIdRef.current!),
      execute: () => window.grecaptcha?.execute(widgetIdRef.current!),
      getResponse: () => window.grecaptcha?.getResponse(widgetIdRef.current!) || '',
    }))

    const renderRecaptcha = useCallback(() => {
      if (!window.grecaptcha || !containerRef.current || widgetIdRef.current !== null) return

      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: ENV.GOOGLE_RECAPTCHA_SITE_KEY,
          callback: (token: string) => onChange(token),
          'expired-callback': () => {
            onChange(null)
            onExpired?.()
          },
          'error-callback': () => {
            onChange(null)
            onError?.()
          },
        })
      } catch {
        onError?.()
      }
    }, [onChange, onError, onExpired])

    // Vite/React-Router (no next/script): inject the reCAPTCHA loader once and
    // render the widget when the library is ready.
    useEffect(() => {
      const onReady = () => window.grecaptcha?.ready(renderRecaptcha)

      if (window.grecaptcha) {
        onReady()
        return
      }

      const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null
      if (existing) {
        existing.addEventListener('load', onReady)
        return () => existing.removeEventListener('load', onReady)
      }

      const script = document.createElement('script')
      script.id = RECAPTCHA_SCRIPT_ID
      script.src = RECAPTCHA_SCRIPT_SRC
      script.async = true
      script.defer = true
      script.addEventListener('load', onReady)
      document.body.appendChild(script)

      return () => script.removeEventListener('load', onReady)
    }, [renderRecaptcha])

    return (
      <Box sx={styles.wrapper}>
        <Box ref={containerRef} sx={styles.container} />
        {errorText && (
          <Text sx={styles.error} component="span">
            {errorText}
          </Text>
        )}
      </Box>
    )
  },
)

ReCaptcha.displayName = 'ReCaptcha'

export default memo(ReCaptcha)
